import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGuard } from '@/features/auth';
import { useItemQuery } from '../hooks/useItemQuery';
import { useItemMutations } from '../hooks/useItemMutations';
import { ItemForm } from '../components/ItemForm';
import type { ItemFormOutput } from '../schemas/item.schema';
import { Alert, TextLink } from '@/shared/components';

interface ItemDetailProps {
    isNew?: boolean;
}

export function ItemDetail({ isNew = false }: ItemDetailProps) {
    const { id } = useParams();
    const navigate = useNavigate();
    const query = useItemQuery(isNew ? undefined : id);
    const { createMutation, updateMutation } = useItemMutations();

    if (!isNew) {
        if (!id) return <p className="text-mp-error">Invalid item ID.</p>;
        if (query.isLoading) return <p>Loading item...</p>;
        if (query.isError || !query.data) {
            return <Alert variant="error"><span>Item not found or failed to load.</span></Alert>;
        }
    }

    const item = query.data;

    const handleSubmit = (data: ItemFormOutput) => {
        if (isNew) {
            createMutation.mutate(data, {
                onSuccess: (created) => navigate(`/smr-admin/items/${created.id}`),
            });
        } else if (id) {
            updateMutation.mutate({ id, payload: data });
        }
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to="/smr-admin/items" color="secondary" className="text-sm">&larr; Back to Items</TextLink>
            </div>
            <h1 className="text-2xl font-semibold">{isNew ? 'New Item' : (item?.name ?? `Item ${id}`)}</h1>

            <PermissionGuard permission={{ resource: 'smr', action: isNew ? 'item.add' : 'item.edit' }}>
                <ItemForm
                    initialValues={item}
                    onSubmit={handleSubmit}
                    isLoading={createMutation.isPending || updateMutation.isPending}
                    submitLabel={isNew ? 'Create Item' : 'Save Changes'}
                />
            </PermissionGuard>
        </section>
    );
}
